import { useAuth0 } from '@auth0/auth0-react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback } from 'react';
import { useParams } from 'react-router-dom';

import { useGetTelehealthSessionInfo } from '../../hooks';
import { useGetCurrentIntentionQuery } from '../../hooks/intentions/useGetCurrentIntentionQuery';
import { useAtomCallbackMemoized } from '../../hooks/useAtomCallbackMemoized';
import { clientUserShortIdAtom } from '../../state';
import { toast } from '../../toasts';
import { DataChannelMessageType } from '../../video-sdk';
import { ProviderUpdatedIntentionToast } from '../intentions/ProviderUpdatedIntentionToast';
import { VisitorDrawerState, visitorDrawerStateAtom } from '../state';

const SESSION_INTENTION_UPDATED_TOAST_KEY = 'session-intention-updated';

export function useHandleReceivedSessionIntentionUpdatedCallback() {
  const { isAuthenticated } = useAuth0();
  const setDrawerState = useSetAtom(visitorDrawerStateAtom);
  const getVisitorDrawerState = useAtomCallbackMemoized((get) => get(visitorDrawerStateAtom), []);
  const { providerShortId, patientShortId } = useParams();
  const { data } = useGetTelehealthSessionInfo(providerShortId, patientShortId);
  const appointmentShortId = data?.telehealthSessionInfo?.appointment?.shortId;
  const clientUserShortId = useAtomValue(clientUserShortIdAtom);

  const { refetch } = useGetCurrentIntentionQuery(clientUserShortId ?? '', providerShortId ?? '', appointmentShortId, {
    skip: true,
  });

  return useCallback(
    async (
      messageType: DataChannelMessageType.SESSION_INTENTION_SET | DataChannelMessageType.SESSION_INTENTION_EDITED,
      sessionIntentionShortId: string,
    ) => {
      const currentDrawerState = getVisitorDrawerState();

      // Close the existing toast that's already showing
      // This can happen if the provider sets the intention
      // & then edits it before the client interacts with the toast
      toast.close(SESSION_INTENTION_UPDATED_TOAST_KEY);

      if (isAuthenticated) {
        await refetch();
      }

      if (currentDrawerState === VisitorDrawerState.INTENTIONS) {
        return;
      }

      toast.enqueue(
        (ariaProps) => (
          <ProviderUpdatedIntentionToast
            messageType={messageType}
            appointmentShortId={appointmentShortId ?? ''}
            sessionIntentionShortId={sessionIntentionShortId}
            onViewClick={() => {
              setDrawerState(VisitorDrawerState.INTENTIONS);
              toast.close(SESSION_INTENTION_UPDATED_TOAST_KEY);
            }}
            {...ariaProps}
          />
        ),
        { key: SESSION_INTENTION_UPDATED_TOAST_KEY },
      );
    },
    [setDrawerState, appointmentShortId, refetch, getVisitorDrawerState, isAuthenticated],
  );
}
